import React, { useState } from "react";
import { isMobile } from "@/stores/MenuStore";
import FinderForm, { StatItem } from "@/components/finder/FinderForm";
import FinderResult from "@/components/finder/FinderResult";
import FinderTeamResult from "@/components/finder/FinderTeamResult";
import { fetchDataWithArgsAfterRefresh } from "@/components/login/loginStore";
import { Button } from "@heroui/button";
import { Icon } from "@iconify/react";
import { useTranslation } from 'react-i18next';

type FinderMode = 'player' | 'team';

const Finder: React.FC = () => {
    const { t } = useTranslation();

    const [finderMode, setFinderMode] = useState<FinderMode>('player');

    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
    const [selectedStat, setSelectedStat] = useState<string>('');
    const [selectedAtLeast, setSelectedAtLeast] = useState<string>('1');
    const [selectedLastGames, setSelectedLastGames] = useState<string>('5');
    const [selectedHitRate, setSelectedHitRate] = useState<number>(0.4);
    const [statName, setStatName] = useState<string | undefined>('');
    const [leaguesNames, setLeaguesNames] = useState<string[] | undefined>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [tableResult, setTableResult] = useState<[]>([]);

    const [seeResult, setSeeResult] = useState<boolean>(false);

    const now = new Date();

    // Get the timezone offset in minutes
    const offsetInMinutes = -now.getTimezoneOffset();

    const teamStats: StatItem[] = [
        { id: 42, icon: 'game-icons:soccer-kick', key: 'shots_total', label: t('finder_team.shots_total'), at_least: 10 },
        { id: 86, icon: 'emojione-monotone:goal-net', key: 'shots_on_target', label: t('finder_team.shots_on_target'), at_least: 10 },
        { id: 52, icon: 'emojione-monotone:goal-net', key: 'goals', label: t('finder_team.goals'), at_least: 10 },
        { id: 34, icon: 'mdi:flag-variant', key: 'corners', label: t('finder_team.corners'), at_least: 10 },
        { id: 84, icon: 'mdi:card', key: 'yellow_cards', label: t('finder_team.yellow_cards'), at_least: 10 },
        { id: 56, icon: 'mdi:whistle', key: 'fouls', label: t('finder_team.fouls'), at_least: 10 },
        { id: 51, icon: 'mdi:flag-off', key: 'offsides', label: t('finder_team.offsides'), at_least: 10 },
        { id: 45, icon: 'mdi:percent', key: 'possession', label: t('finder_team.possession'), at_least: 100 },
        { id: 1042, icon: 'game-icons:soccer-kick', key: 'shots_ht', label: t('finder_team.shots_ht'), at_least: 10 },
        { id: 1034, icon: 'mdi:flag-variant', key: 'corners_ht', label: t('finder_team.corners_ht'), at_least: 10 },
    ];

    const handleModeChange = (mode: FinderMode) => {
        if (mode !== finderMode) {
            setFinderMode(mode);
            setTableResult([]);
            setSeeResult(false);
            setSelectedStat('');
            setSelectedAtLeast('1');
        }
    };

    const getStats = (dates: string[], leagues: string[], stat: string, atLeast: string, lastGames: string, hitRate: number, statName: string | undefined, leaguesNames: string[] | undefined) => {
        setLoading(true);
        const params: any = {
            dates: dates,
            leagues_ids: leagues,
            stat: Number(stat),
            min_value: Number(atLeast),
            game_count: Number(lastGames),
            hit_rate: Number(hitRate * 100),
            tz_offset: offsetInMinutes
        };
        if (finderMode === 'team') {
            params.type = 'team';
        }
        fetchDataWithArgsAfterRefresh(
            ["/api/props_finder", "/not_premium_endpoints/finder_result.json"],
            params,
            (data) => {
                setTableResult(data);
                setStatName(statName);
                setLeaguesNames(leaguesNames);
                setSeeResult(true);
                setLoading(false);
            }, true, false,
            () => { setLoading(false) }
        );
    };

    const modeToggle = (
        <div className="flex w-full max-w-lg gap-1 rounded-medium bg-default-100 p-1">
            <Button
                className={`flex-1 font-medium text-sm ${finderMode === 'player' ? 'bg-primary-500 text-white shadow-md' : 'bg-transparent text-default-600'}`}
                size="sm"
                onClick={() => handleModeChange('player')}
            >
                <Icon icon="solar:user-outline" className="text-lg mr-1" />
                {t('finder_team.tab_player')}
            </Button>
            <Button
                className={`flex-1 font-medium text-sm ${finderMode === 'team' ? 'bg-primary-500 text-white shadow-md' : 'bg-transparent text-default-600'}`}
                size="sm"
                onClick={() => handleModeChange('team')}
            >
                <Icon icon="solar:users-group-two-rounded-outline" className="text-lg mr-1" />
                {t('finder_team.tab_team')}
            </Button>
        </div>
    );

    const finderForm = (
        <div className="flex flex-col gap-3 items-center">
            {modeToggle}
            <FinderForm
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                selectedLeagues={selectedLeagues}
                setSelectedLeagues={setSelectedLeagues}
                selectedStat={selectedStat}
                setSelectedStat={setSelectedStat}
                selectedAtLeast={selectedAtLeast}
                setSelectedAtLeast={setSelectedAtLeast}
                selectedLastGames={selectedLastGames}
                setSelectedLastGames={setSelectedLastGames}
                selectedHitRate={selectedHitRate}
                setSelectedHitRate={setSelectedHitRate}
                getPlayerStats={getStats}
                mode={finderMode}
                externalStats={finderMode === 'team' ? teamStats : undefined}
            />
        </div>
    )

    const finderResult = finderMode === 'team' ? (
        <FinderTeamResult
            tableResult={tableResult}
            leaguesNames={leaguesNames}
            statName={statName}
            atLeast={selectedAtLeast}
            lastGames={selectedLastGames}
            hitRate={selectedHitRate}
            seeResult={seeResult}
            loading={loading}
        />
    ) : (
        <FinderResult
            tableResult={tableResult}
            leaguesNames={leaguesNames}
            statName={statName}
            atLeast={selectedAtLeast}
            lastGames={selectedLastGames}
            hitRate={selectedHitRate}
            seeResult={seeResult}
            loading={loading}
        />
    );

    return (
        <div className={`flex gap-5 w-full ${isMobile() && 'flex-col items-center'}`}>
            {isMobile() ?
                <>
                    {!seeResult ?
                        finderForm
                        :
                        <div className="flex flex-col w-full gap-3 fadeIn">
                            <Button
                                onClick={() => setSeeResult(false)}
                                className="w-[30px] bg-default-200 dark:bg-default-100"
                            >
                                <Icon className="text-2xl" icon="material-symbols:keyboard-backspace" />
                            </Button>
                            {finderResult}
                        </div>
                    }
                </>
                :
                <>
                    <div>{finderForm}</div>
                    <div>{finderResult}</div>
                </>
            }
        </div>
    );
};

export default Finder;
